// src/pages/Public/Contact.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-hot-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://chineafriquecargo.com';
const WHATSAPP_CACHE_KEY = 'cac_whatsapp_number';
const WHATSAPP_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 heures

const Contact = () => {
    const [animated, setAnimated] = useState(false);
    const [whatsappNumber, setWhatsappNumber] = useState('13825016747');
    const [loadingWhatsapp, setLoadingWhatsapp] = useState(true);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);

    useEffect(() => {
        setAnimated(true);
        fetchWhatsappNumber();
    }, []);

    const fetchWhatsappNumber = useCallback(async () => {
        // Vérifier le cache
        const cached = localStorage.getItem(WHATSAPP_CACHE_KEY);
        const cachedTime = localStorage.getItem(`${WHATSAPP_CACHE_KEY}_time`);

        if (cached && cachedTime && (Date.now() - parseInt(cachedTime)) < WHATSAPP_CACHE_DURATION) {
            setWhatsappNumber(cached);
            setLoadingWhatsapp(false);
            return;
        }

        try {
            const response = await axios.get(`${API_URL}/whatsapp/public/`);
            let numbers = [];
            if (response.data && response.data.results) {
                numbers = response.data.results;
            } else if (Array.isArray(response.data)) {
                numbers = response.data;
            }

            if (numbers.length > 0) {
                const defaultNum = numbers.find(n => n.is_default) || numbers[0];
                const cleanNumber = defaultNum.phone_number.replace(/\s+/g, '').replace(/[^0-9+]/g, '');
                setWhatsappNumber(cleanNumber);
                localStorage.setItem(WHATSAPP_CACHE_KEY, cleanNumber);
                localStorage.setItem(`${WHATSAPP_CACHE_KEY}_time`, Date.now().toString());
            }
        } catch (err) {
            console.error('Erreur chargement WhatsApp:', err);
            // Garder le numéro par défaut
        } finally {
            setLoadingWhatsapp(false);
        }
    }, []);

    const cleanPhoneNumber = (phone) => {
        return phone.replace(/\s+/g, '').replace(/[^0-9+]/g, '');
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Le nom est requis';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'L\'email est requis';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Email invalide';
        }

        if (!formData.subject) {
            newErrors.subject = 'Veuillez sélectionner un sujet';
        }

        if (!formData.message.trim()) {
            newErrors.message = 'Le message est requis';
        } else if (formData.message.trim().length < 10) {
            newErrors.message = 'Le message doit contenir au moins 10 caractères';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        // Effacer l'erreur du champ modifié
        if (errors[name]) {
            setErrors({ ...errors, [name]: null });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error('Veuillez corriger les erreurs du formulaire');
            return;
        }

        setSubmitting(true);
        setSubmitSuccess(false);

        try {
            await axios.post(`${API_URL}/api/contact/`, formData);

            toast.success('✅ Message envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.');
            setSubmitSuccess(true);
            setFormData({
                name: '',
                email: '',
                phone: '',
                subject: '',
                message: ''
            });
            setErrors({});
        } catch (err) {
            console.error('Erreur envoi formulaire:', err);

            if (err.response && err.response.data) {
                const apiErrors = err.response.data;
                const newErrors = {};

                // Mapper les erreurs de l'API aux champs
                Object.keys(apiErrors).forEach(key => {
                    if (key in formData) {
                        newErrors[key] = Array.isArray(apiErrors[key])
                            ? apiErrors[key][0]
                            : apiErrors[key];
                    }
                });

                if (Object.keys(newErrors).length > 0) {
                    setErrors(newErrors);
                    toast.error('Veuillez corriger les erreurs du formulaire');
                } else {
                    toast.error(apiErrors.detail || 'Une erreur est survenue. Veuillez réessayer.');
                }
            } else {
                toast.error('Erreur de connexion. Veuillez vérifier votre internet et réessayer.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const contactMethods = [
        {
            icon: '💬',
            title: 'WhatsApp',
            value: whatsappNumber,
            action: `https://wa.me/${cleanPhoneNumber(whatsappNumber)}`,
            color: '#25D366'
        },
        {
            icon: '📧',
            title: 'Email',
            value: 'contact@cac-int.com',
            action: 'mailto:contact@cac-int.com',
            color: '#C62828'
        },
        {
            icon: '📍',
            title: 'Adresse Chine',
            value: '佛山市南海区里水镇湖州村湖州大道10号',
            color: '#0f1685'
        },
        {
            icon: '⏰',
            title: 'Horaires',
            value: 'Lun-Sam: 9:30-20:00 | Dim: 9:30-18:00',
            color: '#F9A825'
        }
    ];

    const africaOffices = [
        { country: 'Gabon', phone: '+241 07 38 59 57', rawPhone: '+24107385957', flag: '🇬🇦', color: '#2E7D32' },
        { country: 'Mali', phone: '+223 83 91 61 31 / 72 02 16 96', rawPhone: '+22383916131', flag: '🇲🇱', color: '#F9A825' },
        { country: 'Congo', phone: '+242 05 39 49 494 / 06 83 88 383', rawPhone: '+242053949494', flag: '🇨🇬', color: '#1565C0' },
        { country: 'Côte d\'Ivoire', phone: '+225 07 58 58 11 55 / 07 48 47 80 57', rawPhone: '+2250758581155', flag: '🇨🇮', color: '#F57F17' }
    ];

    if (loadingWhatsapp) {
        return (
            <div className="skeleton-loader">
                <div className="skeleton-spinner"></div>
                <style>{`
                    .skeleton-loader {
                        min-height: 100vh;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: linear-gradient(135deg, #f5f7fa 0%, #f0f2f5 100%);
                    }
                    .skeleton-spinner {
                        width: 50px;
                        height: 50px;
                        border: 3px solid rgba(198,40,40,0.1);
                        border-top-color: #C62828;
                        border-radius: 50%;
                        animation: spin 1s linear infinite;
                    }
                    @keyframes spin {
                        to { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    return (
        <div className="contact-page">
            <Helmet>
                <title>Contactez CAC | Chine Afrique Cargo</title>
                <meta name="description" content="Contactez CAC pour tous vos besoins de transport entre la Chine et l'Afrique. Demande de devis, suivi de colis, assistance. WhatsApp, Email, Téléphone." />
                <meta name="keywords" content="contact CAC, transport chine afrique, devis fret, logistique, whatsapp CAC" />
                <meta name="author" content="CAC - Chine Afrique Cargo" />
                <meta name="robots" content="index, follow" />
                <link rel="canonical" href="https://cac-int.com/contact" />
            </Helmet>

            {/* Hero Section */}
            <div className="contact-hero">
                <div className="hero-overlay"></div>
                <Container className="hero-container">
                    <div className={`hero-content ${animated ? 'fade-in-up' : ''}`}>
                        <div className="hero-badge">
                            <span className="badge-pulse"> Contactez-nous</span>
                        </div>
                        <h1 className="hero-title">
                            Parlons de votre <span className="text-gradient">projet</span>
                        </h1>
                        <p className="hero-subtitle">
                            Notre équipe est à votre disposition pour répondre à toutes vos questions
                        </p>
                    </div>
                </Container>
                <div className="hero-wave">
                    <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 64L60 69.3C120 75 240 85 360 80C480 75 600 53 720 48C840 43 960 53 1080 58.7C1200 64 1320 64 1380 64L1440 64L1440 120L1380 120C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120L0 120Z" fill="#f8f9fa" />
                    </svg>
                </div>
            </div>

            <Container className="contact-container">
                <Row className="g-5">
                    {/* Contact Form */}
                    <Col lg={6}>
                        <div className={`contact-form-card ${animated ? 'slide-right' : ''}`}>
                            <div className="form-header">
                                <div className="form-icon">✉️</div>
                                <h2>Envoyez-nous un message</h2>
                                <p>Nous vous répondrons dans les plus brefs délais</p>
                            </div>

                            {submitSuccess && (
                                <div className="success-message">
                                    <span>✅</span> Votre message a été envoyé avec succès !
                                </div>
                            )}

                            <Form onSubmit={handleSubmit} noValidate>
                                <Form.Group className="form-group">
                                    <Form.Label visuallyHidden>Nom complet</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Nom complet *"
                                        className={`form-control-custom ${errors.name ? 'is-invalid' : ''}`}
                                        aria-label="Nom complet"
                                        aria-required="true"
                                        aria-invalid={!!errors.name}
                                    />
                                    {errors.name && (
                                        <div className="error-message">{errors.name}</div>
                                    )}
                                </Form.Group>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="form-group">
                                            <Form.Label visuallyHidden>Email</Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                placeholder="Email *"
                                                className={`form-control-custom ${errors.email ? 'is-invalid' : ''}`}
                                                aria-label="Email"
                                                aria-required="true"
                                                aria-invalid={!!errors.email}
                                            />
                                            {errors.email && (
                                                <div className="error-message">{errors.email}</div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="form-group">
                                            <Form.Label visuallyHidden>Téléphone</Form.Label>
                                            <Form.Control
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                placeholder="Téléphone (optionnel)"
                                                className="form-control-custom"
                                                aria-label="Téléphone"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="form-group">
                                    <Form.Label visuallyHidden>Sujet</Form.Label>
                                    <Form.Select
                                        name="subject"
                                        value={formData.subject}
                                        onChange={handleChange}
                                        className={`form-control-custom ${errors.subject ? 'is-invalid' : ''}`}
                                        aria-label="Sujet"
                                        aria-required="true"
                                        aria-invalid={!!errors.subject}
                                    >
                                        <option value="">Sélectionnez un sujet *</option>
                                        <option value="devis">📦 Demande de devis</option>
                                        <option value="suivi">📍 Suivi de colis</option>
                                        <option value="reclamation">⚠️ Réclamation</option>
                                        <option value="partenariat">🤝 Partenariat</option>
                                        <option value="autre">💬 Autre</option>
                                    </Form.Select>
                                    {errors.subject && (
                                        <div className="error-message">{errors.subject}</div>
                                    )}
                                </Form.Group>

                                <Form.Group className="form-group">
                                    <Form.Label visuallyHidden>Message</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={5}
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        placeholder="Votre message *"
                                        className={`form-control-custom ${errors.message ? 'is-invalid' : ''}`}
                                        aria-label="Message"
                                        aria-required="true"
                                        aria-invalid={!!errors.message}
                                    />
                                    {errors.message && (
                                        <div className="error-message">{errors.message}</div>
                                    )}
                                </Form.Group>

                                <Button
                                    type="submit"
                                    disabled={submitting}
                                    className="submit-btn"
                                    aria-label="Envoyer le message"
                                >
                                    {submitting ? (
                                        <>
                                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                            <span className="ms-2">Envoi en cours...</span>
                                        </>
                                    ) : (
                                        'Envoyer le message →'
                                    )}
                                </Button>
                            </Form>
                        </div>
                    </Col>

                    {/* Contact Info */}
                    <Col lg={6}>
                        <div className={`contact-info-card ${animated ? 'slide-left' : ''}`}>
                            <div className="info-header">
                                <div className="info-icon">📍</div>
                                <h2>Nos coordonnées</h2>
                                <p>Plusieurs moyens de nous contacter</p>
                            </div>

                            <div className="contact-methods">
                                {contactMethods.map((method, idx) => (
                                    <div key={idx} className="method-card">
                                        <div
                                            className="method-icon"
                                            style={{ backgroundColor: `${method.color}15`, color: method.color }}
                                            aria-hidden="true"
                                        >
                                            {method.icon}
                                        </div>
                                        <div className="method-info">
                                            <h6>{method.title}</h6>
                                            {method.action ? (
                                                <a
                                                    href={method.action}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label={`Contacter via ${method.title}`}
                                                >
                                                    {method.value}
                                                </a>
                                            ) : (
                                                <span>{method.value}</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="africa-section">
                                <h3>🌍 Bureaux Afrique</h3>
                                <div className="africa-grid">
                                    {africaOffices.map((office, idx) => (
                                        <div key={idx} className="office-card">
                                            <div className="office-flag" aria-hidden="true">{office.flag}</div>
                                            <div className="office-info">
                                                <strong>{office.country}</strong>
                                                <a
                                                    href={`https://wa.me/${cleanPhoneNumber(office.rawPhone)}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="office-phone-link"
                                                    aria-label={`Contacter le bureau ${office.country} via WhatsApp`}
                                                >
                                                    {office.phone}
                                                </a>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="address-card">
                                <div className="address-icon" aria-hidden="true">🏢</div>
                                <div className="address-info">
                                    <strong>📍 Entrepôt Chine</strong>
                                    <p>佛山市南海区里水镇湖州村湖州大道10号</p>
                                    <small>(导航：锦丰压花冲孔)</small>
                                </div>
                            </div>

                            <div className="social-links">
                                <a
                                    href={`https://wa.me/${cleanPhoneNumber(whatsappNumber)}`}
                                    className="social-link whatsapp"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Nous contacter sur WhatsApp"
                                >
                                    <span aria-hidden="true">💬</span> WhatsApp
                                </a>
                                <a
                                    href="mailto:contact@cac-int.com"
                                    className="social-link email"
                                    aria-label="Nous envoyer un email"
                                >
                                    <span aria-hidden="true">📧</span> Email
                                </a>
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Map Section */}
                <div className={`map-section ${animated ? 'fade-in-up' : ''}`}>
                    <Card className="map-card">
                        <Card.Body className="p-0">
                            <iframe
                                title="Localisation CAC Chine"
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3688.123456789012!2d113.118456!3d23.125678!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3402f123456789ab%3A0x123456789abcdef!2sFoshan%2C%20Guangdong%2C%20China!5e0!3m2!1sen!2sfr!4v1234567890123"
                                width="100%"
                                height="400"
                                style={{ border: 0 }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                aria-label="Carte de localisation de CAC en Chine"
                            />
                        </Card.Body>
                    </Card>
                    <p className="map-note">
                        * Adresse exacte : 佛山市南海区里水镇湖州村湖州大道10号 (导航：锦丰压花冲孔)
                    </p>
                </div>
            </Container>

            <style>{`
                .contact-page {
                    background: #f8f9fa;
                    min-height: 100vh;
                }

                /* Hero Section */
                .contact-hero {
                    position: relative;
                    background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
                    min-height: 400px;
                    display: flex;
                    align-items: center;
                    overflow: hidden;
                }

                .hero-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: radial-gradient(circle at 20% 50%, rgba(198, 40, 40, 0.2) 0%, transparent 50%);
                }

                .hero-container {
                    position: relative;
                    z-index: 2;
                    padding: 80px 0;
                }

                .hero-content {
                    text-align: center;
                    max-width: 700px;
                    margin: 0 auto;
                }

                .hero-badge {
                    margin-bottom: 24px;
                }

                .badge-pulse {
                    display: inline-block;
                    padding: 8px 20px;
                    background: rgba(249, 168, 37, 0.15);
                    border: 1px solid rgba(249, 168, 37, 0.3);
                    border-radius: 50px;
                    font-size: 14px;
                    font-weight: 600;
                    color: #F9A825;
                    animation: pulse 2s infinite;
                }

                @keyframes pulse {
                    0% { box-shadow: 0 0 0 0 rgba(249, 168, 37, 0.4); }
                    70% { box-shadow: 0 0 0 10px rgba(249, 168, 37, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(249, 168, 37, 0); }
                }

                .hero-title {
                    font-size: 56px;
                    font-weight: 800;
                    margin-bottom: 20px;
                    color: white;
                }

                .text-gradient {
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }

                .hero-subtitle {
                    font-size: 18px;
                    color: rgba(255, 255, 255, 0.7);
                }

                .hero-wave {
                    position: absolute;
                    bottom: -1px;
                    left: 0;
                    right: 0;
                }

                /* Contact Container */
                .contact-container {
                    padding: 60px 0;
                }

                /* Success Message */
                .success-message {
                    background: linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%);
                    border: 1px solid #4CAF50;
                    border-radius: 12px;
                    padding: 16px;
                    margin-bottom: 24px;
                    text-align: center;
                    color: #2E7D32;
                    font-weight: 500;
                    animation: slideDown 0.4s ease;
                }

                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                /* Error Message */
                .error-message {
                    color: #C62828;
                    font-size: 13px;
                    margin-top: 6px;
                    padding-left: 4px;
                    animation: fadeIn 0.3s ease;
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                /* Contact Form Card */
                .contact-form-card {
                    background: white;
                    border-radius: 24px;
                    padding: 40px;
                    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.05);
                }

                .slide-right {
                    animation: slideRight 0.6s ease forwards;
                }

                @keyframes slideRight {
                    from {
                        opacity: 0;
                        transform: translateX(-30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }

                .form-header {
                    text-align: center;
                    margin-bottom: 30px;
                }

                .form-icon {
                    font-size: 48px;
                    margin-bottom: 16px;
                }

                .form-header h2 {
                    font-size: 28px;
                    font-weight: 700;
                    margin-bottom: 8px;
                    color: #C62828;
                }

                .form-header p {
                    color: #666;
                }

                .form-group {
                    margin-bottom: 20px;
                }

                .form-control-custom {
                    width: 100%;
                    padding: 14px 20px;
                    border: 2px solid #e0e0e0;
                    border-radius: 12px;
                    font-size: 14px;
                    transition: all 0.3s ease;
                    background: white;
                }

                .form-control-custom:focus {
                    border-color: #C62828;
                    outline: none;
                    box-shadow: 0 0 0 3px rgba(198, 40, 40, 0.1);
                }

                .form-control-custom.is-invalid {
                    border-color: #C62828;
                    background-color: #FFF5F5;
                }

                .submit-btn {
                    width: 100%;
                    padding: 14px;
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    border: none;
                    border-radius: 12px;
                    color: white;
                    font-weight: 600;
                    font-size: 16px;
                    transition: all 0.3s ease;
                }

                .submit-btn:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 25px rgba(198, 40, 40, 0.4);
                }

                .submit-btn:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }

                /* Contact Info Card */
                .contact-info-card {
                    background: white;
                    border-radius: 24px;
                    padding: 40px;
                    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.05);
                }

                .slide-left {
                    animation: slideLeft 0.6s ease forwards;
                }

                @keyframes slideLeft {
                    from {
                        opacity: 0;
                        transform: translateX(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }

                .info-header {
                    text-align: center;
                    margin-bottom: 30px;
                }

                .info-icon {
                    font-size: 48px;
                    margin-bottom: 16px;
                }

                .info-header h2 {
                    font-size: 28px;
                    font-weight: 700;
                    margin-bottom: 8px;
                    color: #C62828;
                }

                .info-header p {
                    color: #666;
                }

                .contact-methods {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                    margin-bottom: 30px;
                }

                .method-card {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    padding: 16px;
                    background: #f8f9fa;
                    border-radius: 16px;
                    transition: all 0.3s ease;
                }

                .method-card:hover {
                    transform: translateX(5px);
                    background: white;
                    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
                }

                .method-icon {
                    width: 50px;
                    height: 50px;
                    border-radius: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                }

                .method-info h6 {
                    margin: 0 0 4px 0;
                    font-weight: 600;
                }

                .method-info a, 
                .method-info span {
                    color: #666;
                    text-decoration: none;
                    font-size: 14px;
                    transition: color 0.3s ease;
                }

                .method-info a:hover {
                    color: #C62828;
                    text-decoration: underline;
                }

                .africa-section {
                    margin-bottom: 30px;
                }

                .africa-section h3 {
                    font-size: 18px;
                    font-weight: 700;
                    margin-bottom: 20px;
                    color: #C62828;
                }

                .africa-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 12px;
                }

                .office-card {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 12px;
                    background: #f8f9fa;
                    border-radius: 12px;
                    transition: all 0.3s ease;
                }

                .office-card:hover {
                    transform: translateY(-2px);
                    background: white;
                    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
                }

                .office-flag {
                    font-size: 28px;
                }

                .office-info {
                    display: flex;
                    flex-direction: column;
                }

                .office-info strong {
                    font-size: 14px;
                }

                .office-phone-link {
                    font-size: 12px;
                    color: #666;
                    text-decoration: none;
                    transition: color 0.3s ease;
                }

                .office-phone-link:hover {
                    color: #25D366;
                    text-decoration: underline;
                }

                .address-card {
                    display: flex;
                    gap: 16px;
                    padding: 20px;
                    background: linear-gradient(135deg, #FFF8E1 0%, #FFECB3 100%);
                    border-radius: 16px;
                    margin-bottom: 30px;
                }

                .address-icon {
                    font-size: 32px;
                }

                .address-info strong {
                    display: block;
                    margin-bottom: 8px;
                }

                .address-info p {
                    margin: 0;
                    color: #666;
                    font-size: 14px;
                }

                .address-info small {
                    color: #999;
                    font-size: 12px;
                }

                .social-links {
                    display: flex;
                    gap: 12px;
                    justify-content: center;
                }

                .social-link {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 20px;
                    border-radius: 40px;
                    text-decoration: none;
                    font-weight: 600;
                    font-size: 14px;
                    transition: all 0.3s ease;
                }

                .social-link.whatsapp {
                    background: #25D366;
                    color: white;
                }

                .social-link.email {
                    background: #C62828;
                    color: white;
                }

                .social-link.wechat {
                    background: #07C160;
                    color: white;
                }

                .social-link:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
                    color: white;
                }

                /* Map Section */
                .map-section {
                    margin-top: 40px;
                }

                .map-card {
                    border: none;
                    border-radius: 24px;
                    overflow: hidden;
                    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.05);
                }

                .map-note {
                    text-align: center;
                    margin-top: 12px;
                    color: #666;
                    font-size: 13px;
                }

                .fade-in-up {
                    animation: fadeInUp 0.6s ease forwards;
                }

                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                /* Responsive */
                @media (max-width: 992px) {
                    .hero-title {
                        font-size: 40px;
                    }

                    .contact-form-card, 
                    .contact-info-card {
                        padding: 30px;
                    }

                    .form-header h2, 
                    .info-header h2 {
                        font-size: 24px;
                    }
                }

                @media (max-width: 768px) {
                    .hero-title {
                        font-size: 32px;
                    }

                    .africa-grid {
                        grid-template-columns: 1fr;
                    }

                    .contact-methods {
                        gap: 12px;
                    }

                    .social-links {
                        flex-direction: column;
                    }

                    .social-link {
                        justify-content: center;
                    }

                    .contact-form-card, 
                    .contact-info-card {
                        padding: 24px;
                    }

                    .map-card iframe {
                        height: 300px;
                    }
                }

                @media (max-width: 576px) {
                    .hero-title {
                        font-size: 28px;
                    }

                    .hero-subtitle {
                        font-size: 16px;
                    }

                    .form-control-custom {
                        padding: 12px 16px;
                    }
                }
            `}</style>
        </div>
    );
};

export default Contact;